export function getMatterDisplayFlags(data) {
    const cfg = data?.config?.matter_display;
    const showNumber = cfg?.show_number;
    const showAltNumber = cfg?.show_alt_number;
    // XPEN-3323: independent of the identifier flags — a tenant may prefix the
    // ERP client's name onto whichever identifier the two flags below select.
    const showClientName = cfg?.show_client_name === true;
    const bothAbsent = showNumber === undefined && showAltNumber === undefined;
    // Both false is invalid (there'd be nothing to render). Fall through to the
    // number-only default rather than emit an empty label.
    const bothFalse = showNumber === false && showAltNumber === false;
    if (bothAbsent || bothFalse) {
        // Carries show_client_name through: the expected rollout is a tenant
        // setting show_client_name alone, which leaves both identifier flags
        // absent and lands here.
        return { show_number: true, show_alt_number: false, show_client_name: showClientName };
    }
    return {
        show_number: showNumber !== false,
        show_alt_number: showAltNumber === true,
        show_client_name: showClientName,
    };
}

// XPEN-3322: SurePoint (Nilan Johnson) matter numbers carry a sub-client segment
// that is always "000" — `56585-000-0001`. The firm reads and searches matters
// without it, so the UI drops it.
//
// Deliberately narrow: exactly three dash-separated parts whose middle part is
// all zeros. That leaves every other tenant's shape untouched — Elite's
// `324246.0001` (dot), BCF's `10482` (no separator), Aderant's `A-1` (two
// parts) — and it can never collapse two matters onto one label, because a real
// sub-client (`-001`) stays visible.
//
// Display only. `matters.number` and every stored `MatterId` keep the full
// value, which is what the ERP adapters resolve against
// (surepoint_adapter._matter, aderant_adapter._resolve_matter).
const COLLAPSIBLE_MATTER_NUMBER = /^([^-]+)-0+-([^-]+)$/;

export function formatMatterNumber(value) {
    if (value == null) return value;
    const match = COLLAPSIBLE_MATTER_NUMBER.exec(String(value).trim());
    return match ? `${match[1]}-${match[2]}` : value;
}

// Inverse of formatMatterNumber, resolved against the suggestions already
// fetched — never a stored value the picker has not seen. Used on the free-solo
// blur save so a user who types the collapsed number persists the full one; a
// collapsed `MatterId` would resolve to nothing at export time.
export function expandMatterNumber(text, suggestions = []) {
    const typed = String(text ?? "").trim();
    if (!typed) return text;

    const identifiers = [];
    for (const suggestion of suggestions ?? []) {
        for (const candidate of [suggestion?.name, suggestion?.altNumber]) {
            const raw = canonicalize(candidate);
            if (raw) identifiers.push(raw);
        }
    }

    // Already a stored identifier: leave it exactly as typed. XPEN-3080 lets a
    // line be stored as the altNumber, and that must survive untouched.
    if (identifiers.includes(typed)) return text;

    const expanded = identifiers.filter((raw) => formatMatterNumber(raw) === typed);
    // Only when unambiguous — two matters collapsing onto the same typed text
    // must not silently resolve to whichever the search happened to return first.
    return expanded.length === 1 ? expanded[0] : text;
}

function readIdentifiers(matter) {
    // XPEN-3322: collapsed here so every label variant, every flag branch and
    // the client-name prefix below all inherit it from one place.
    const number = formatMatterNumber(matter?.name ?? matter?.number ?? "");
    const altNumber = formatMatterNumber(matter?.altNumber ?? "");
    const description = matter?.description ?? "";
    // XPEN-3323: absent on every tenant whose ERP sync doesn't write it, and
    // null on Juris matters created before the field was populated.
    const clientName = matter?.clientName ?? "";
    return { number, altNumber, description, clientName };
}

function canonicalize(value) {
    return value == null ? "" : String(value).trim();
}

function shouldPairBoth(number, altNumber, flags) {
    const showNumber = flags?.show_number !== false;
    const showAltNumber = flags?.show_alt_number === true;
    if (!showNumber || !showAltNumber) return false;
    const canonicalAlt = canonicalize(altNumber);
    const canonicalNum = canonicalize(number);
    return canonicalAlt.length > 0 && canonicalAlt !== canonicalNum;
}

// XPEN-3323: `<clientName> - <label>` when the tenant shows the ERP client's
// name. Prefixing the finished label keeps it orthogonal to the number /
// altNumber flags. A blank or missing name yields the label untouched — never
// an orphan " - " — which is the case for every tenant whose sync doesn't
// write clientName.
function withClientName(label, clientName, flags) {
    if (flags?.show_client_name !== true) return label;
    const canonicalName = canonicalize(clientName);
    return canonicalName ? `${canonicalName} - ${label}` : label;
}

// AutoCompleteField / MUI Autocomplete label: `<head> (<description>)`,
// or `<number> (<altNumber> - <description>)` when both are shown.
export function formatMatterLabel(matter, flags = { show_number: true, show_alt_number: false }) {
    if (typeof matter === "string") return formatMatterNumber(matter);
    const { number, altNumber, description, clientName } = readIdentifiers(matter || {});

    if (shouldPairBoth(number, altNumber, flags)) {
        return withClientName(
            `${number} (${altNumber}${description ? " - " + description : ""})`,
            clientName,
            flags,
        );
    }

    const showAltOnly = flags?.show_number === false && flags?.show_alt_number === true;
    const head = showAltOnly ? (canonicalize(altNumber) ? altNumber : number) : number;
    return withClientName(`${head}${description ? ` (${description})` : ""}`, clientName, flags);
}

// react-select / inline text label: `<head> - <description>` — matches
// the ` - ` delimiter used by sibling pickers (loadCostTypes, loadActivities,
// office-option-label, additional-fields-modal, invoice-approval-modal).
export function formatMatterInlineLabel(matter, flags = { show_number: true, show_alt_number: false }) {
    if (typeof matter === "string") return formatMatterNumber(matter);
    const { number, altNumber, description, clientName } = readIdentifiers(matter || {});

    if (shouldPairBoth(number, altNumber, flags)) {
        return withClientName(
            `${number} - ${altNumber}${description ? " - " + description : ""}`,
            clientName,
            flags,
        );
    }

    const showAltOnly = flags?.show_number === false && flags?.show_alt_number === true;
    const head = showAltOnly ? (canonicalize(altNumber) ? altNumber : number) : number;
    return withClientName(`${head}${description ? ` - ${description}` : ""}`, clientName, flags);
}

export function formatMatterLabelFromData(data, matter) {
    return formatMatterLabel(matter, getMatterDisplayFlags(data));
}

export function formatMatterInlineLabelFromData(data, matter) {
    return formatMatterInlineLabel(matter, getMatterDisplayFlags(data));
}
